import * as React from 'react';

import { faWrench } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { Button, Modal } from '@shared/components/bootstrap';

import { ticketURL } from '@admin/config/routes';
import { client } from '@admin/libraries/apollo';
import { useTechSupportTicketCreateMutation, Ticket__Priority, TechSupportTicketInput } from '@admin/schema';
import { ITicketable } from '@admin/types/ticketable';

import { Area, BusinessUnit, TechSupportForm } from './form';

interface ICreateTicketProps {
  ticketable: ITicketable;
}

export const CreateTicket: React.FC<ICreateTicketProps> = ({ ticketable }) => {
  const [show, setShow] = React.useState(false);
  const [priority, setPriority] = React.useState<Ticket__Priority | undefined>(undefined);
  const [description, setDescription] = React.useState<string | undefined>(undefined);
  const [explanation, setExplanation] = React.useState<string | undefined>(undefined);
  const [notify, setNotify] = React.useState(true);
  const [error, setError] = React.useState<Error | undefined>(undefined);
  const [ticketID, setTicketID] = React.useState<number | undefined>(undefined);
  const [businessUnit, setBusinessUnit] = React.useState<BusinessUnit | undefined>(undefined);
  const [area, setArea] = React.useState<Area | undefined>(undefined);

  const [techSupportTicketCreate, { loading }] = useTechSupportTicketCreateMutation({ client });

  const handleShow = () => setShow(true);
  const handleClose = () => {
    setShow(false);
    setTicketID(undefined);
    setError(undefined);
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    event.stopPropagation();

    // Validate required fields
    if (!priority || !description || !explanation || !businessUnit || !area) {
      setError(new Error('Please fill in all required fields'));
      return;
    }

    const { id, type, feature } = ticketable;
    const explainedDescription = ['Request Description:', description, 'Reason for Request:', explanation].join('\n\n');

    try {
      setError(undefined);

      const input: TechSupportTicketInput = {
        description: explainedDescription,
        notify,
        businessUnit: businessUnit,
        area: area,
        priority: priority,
        ticketableType: type,
        ticketableID: id ? String(id) : undefined,
        ticketableFeatureName: feature,
      };

      const response = await techSupportTicketCreate({
        variables: {
          input,
        },
      });

      if (response.data?.techSupportTicketCreate.ticket?.id) {
        setTicketID(parseInt(response.data.techSupportTicketCreate.ticket.id));
      } else if (response.data?.techSupportTicketCreate.error) {
        setError(new Error(response.data.techSupportTicketCreate.error));
      }
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <>
      <Button type="button" kind="warning" onClick={handleShow}>
        Tech Support <FontAwesomeIcon icon={faWrench} />
      </Button>
      {show && (
        <Modal onClose={handleClose}>
          <Modal.Content>
            <form onSubmit={handleSubmit}>
              <Modal.Header>
                <Modal.Title id="contained-modal-title-lg">
                  Tech Support Request for {ticketable.displayName}
                </Modal.Title>
                <Modal.Close close={handleClose} />
              </Modal.Header>
              <Modal.Body>
                {ticketID ? (
                  <div className="text-center">
                    <h5>Your request has been submitted.</h5>
                    <a className="btn btn-success" href={ticketURL({ id: ticketID })}>
                      View Ticket
                    </a>
                  </div>
                ) : (
                  <TechSupportForm
                    priority={priority}
                    description={description}
                    explanation={explanation}
                    notify={notify}
                    businessUnit={businessUnit}
                    area={area}
                    onPriority={setPriority}
                    onDescription={setDescription}
                    onExplanation={setExplanation}
                    onNotify={setNotify}
                    onBusinessUnit={setBusinessUnit}
                    onArea={setArea}
                    error={error}
                  />
                )}
              </Modal.Body>
              <Modal.Footer>
                {ticketID ? (
                  <button type="button" className="btn btn-primary" onClick={handleClose}>
                    Close
                  </button>
                ) : (
                  <>
                    <button type="button" className="btn btn-danger" onClick={handleClose}>
                      Cancel
                    </button>
                    <button disabled={loading} type="submit" className="btn btn-primary">
                      Submit
                    </button>
                  </>
                )}
              </Modal.Footer>
            </form>
          </Modal.Content>
        </Modal>
      )}
    </>
  );
};
